import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useLang } from "@/i18n";
import { useBooking } from "@/BookingContext";

export default function Footer() {
  const { t, simg } = useLang();
  const { openBooking } = useBooking();

  const links = [
    { to: "/#conciergerie", label: t.nav.concierge, testid: "footer-link-conciergerie" },
    { to: "/services", label: t.nav.services, testid: "footer-link-services" },
    { to: "/locations", label: t.nav.locations, testid: "footer-link-locations" },
    { to: "/prestataires", label: t.nav.providers, testid: "footer-link-prestataires" },
    { to: "/contact", label: t.nav.contact, testid: "footer-link-contact" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-maquis-deep text-casse/80">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-20 pb-8 sm:pb-10">
        <div className="flex flex-col gap-14 sm:flex-row sm:items-center sm:justify-between sm:gap-8 md:gap-10 lg:gap-14">
          <div>
            <img
              src={simg("logoWhite")}
              alt="Presence Home Services"
              data-testid="footer-logo"
              className="h-24 sm:h-24 md:h-24 lg:h-36 w-auto"
            />
          </div>
          <div className="flex flex-col gap-12 sm:flex-row sm:gap-10 md:gap-12 lg:gap-20">
            <div>
              <p className="eyebrow text-sable/70">{t.footer.navigation}</p>
              <ul className="mt-5 space-y-3 text-[13px]">
                {links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} data-testid={l.testid} className="hover:text-casse transition-colors duration-200">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow text-sable/70">Contact</p>
              <ul className="mt-5 space-y-3 text-[13px]">
                <li>Corse-du-Sud, France</li>
                <li>
                  <a href="mailto:caropv2a@gmail.com" data-testid="footer-email" className="hover:text-casse transition-colors duration-200">
                    caropv2a@gmail.com
                  </a>
                </li>
              </ul>
              <button
                data-testid="footer-reserve-button"
                onClick={() => openBooking()}
                className="btn-outline-elegant mt-7 !px-5 !py-2 !text-[11px] border-casse/50 text-casse hover:bg-casse hover:text-maquis-deep"
              >
                {t.footer.reserve}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-14 flex items-center gap-5">
          <a
            href="https://www.instagram.com/presence_home_services"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram"
            aria-label="Instagram"
            className="inline-flex text-casse/60 transition-colors duration-200 hover:text-casse"
          >
            <Instagram size={18} strokeWidth={1.5} />
          </a>
          <a
            href="https://share.google/zrXCIt4Sp3uxre0bV"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-google"
            aria-label="Fiche Google Présence Home Services"
            className="inline-flex text-casse/60 transition-colors duration-200 hover:text-casse"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
            </svg>
          </a>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-casse/15 pt-8 text-[11px] md:flex-nowrap md:text-[10px] lg:text-[11px] text-casse/50">
          <p data-testid="footer-copyright" className="md:whitespace-nowrap lg:whitespace-normal">
            © 2026 Presence Home Services — {t.footer.created_by}{" "}
            <a
              href="https://www.thehillproject.fr"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-agency-link"
              className="text-casse/75 underline decoration-casse/30 underline-offset-4 hover:text-casse transition-colors duration-200"
            >
              THE HILL PROJECT
            </a>
          </p>
          <div className="flex flex-wrap gap-5 md:flex-nowrap lg:flex-wrap">
            <span data-testid="footer-site-vitrine" className="hidden lg:inline">{t.footer.site_vitrine}</span>
            <Link to="/mentions-legales" data-testid="footer-legal" className="hover:text-casse/80 transition-colors">
              {t.footer.legal}
            </Link>
            <Link to="/politique-de-confidentialite" data-testid="footer-privacy" className="hover:text-casse/80 transition-colors">
              {t.footer.privacy}
            </Link>
            <Link to="/espace-proprietaire" data-testid="footer-owner-link" className="hidden lg:inline hover:text-casse/80 transition-colors">
              {t.nav.owner}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
